import { Maximize2Icon, Minimize2Icon, PanelRightCloseIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { AssistantMark } from './assistant-mark';
import { Button } from './button';
import type { ChatPanelMode } from './chat-panel-mode';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

interface ChatPanelHeaderProps {
  title: ReactNode;
  /** What the Assistant can see, e.g. `Dashboard · Data foundation · last 24h`. */
  context?: ReactNode;
  /** Heading id, so the panel landmark can be labelled by it. */
  titleId?: string;
  mode: Exclude<ChatPanelMode, 'collapsed'>;
  onModeChange: (mode: ChatPanelMode) => void;
  /** Extra controls before the mode buttons, e.g. "New chat". */
  actions?: ReactNode;
}

const ICON_BUTTON =
  'size-11 rounded-lg text-[var(--z-text-secondary)] hover:border-border hover:text-foreground sm:size-8';

export const ChatPanelHeader: FC<ChatPanelHeaderProps> = ({
  title,
  context,
  titleId,
  mode,
  onModeChange,
  actions
}) => {
  const isFull = mode === 'full';
  const fullLabel = isFull ? 'Exit full screen' : 'Full screen';

  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-[var(--z-border-subtle)] py-4 pr-[18px] pl-[22px] max-sm:pr-2 max-sm:pl-4">
      <AssistantMark />
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <h2 id={titleId} className="text-subtitle leading-[1.125rem] text-foreground">
          {title}
        </h2>
        {context ? (
          <p className="flex min-w-0 items-center gap-1.5 text-caption whitespace-nowrap text-muted-foreground">
            <span
              aria-hidden="true"
              className="size-1.5 shrink-0 rounded-full bg-success"
            />
            <span className="text-eyebrow-sm font-semibold tracking-[0.08em] uppercase">
              Context
            </span>
            <span className="truncate">{context}</span>
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {actions}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={fullLabel}
              className={ICON_BUTTON}
              onClick={() => onModeChange(isFull ? 'docked' : 'full')}
            >
              {isFull ? <Minimize2Icon /> : <Maximize2Icon />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{fullLabel}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Collapse"
              className={ICON_BUTTON}
              onClick={() => onModeChange('collapsed')}
            >
              <PanelRightCloseIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Collapse</TooltipContent>
        </Tooltip>
      </div>
    </header>
  );
};
